import React, { Component, ReactNode } from 'react';
import { Platform, DeviceType, WriteDirection, Orientation } from 'sofa-reborn-react';
import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import {
	ConfirmationSummaryShareDesignStateEnum,
	ConfigurationSummaryConfirmationCardTypeEnum,
} from '@inter-ikea-kompis/component-configuration-summary';
import { IncorrectIcon } from '@inter-ikea-kompis/icons';
import { localeService } from '../../services';
import { LoadingLightboxContent, ErrorLightboxContent } from './SaveViewStyle';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import SkapaModal, {
	Sheets as SkapaSheets,
	ModalHeader as SkapaModalHeader,
	ModalBody as SkapaModalBody,
} from '@ingka/modal';
import Loading, { LoadingBall } from '@ingka/loading';
import { KompisText, KompisShareDesignCard, KompisIcon } from '@inter-ikea-kompis/react-components';
import {
	ShareDesignCardStateEnum,
	ShareDesignCardCopyStateEnum,
} from '@inter-ikea-kompis/component-share-design-card';
import { SendByEmailStateEnum } from '@inter-ikea-kompis/component-send-by-email';

type Props = {
	platform: Platform;
	deviceType: DeviceType;
	orientation: Orientation;
	writeDirection: WriteDirection;
	vpcCode: string;
	kompisSettings?: IDexfSettings | null;
	kompisTranslations?: ITranslations | null;
	locale: string;
	confirmationCardType: ConfigurationSummaryConfirmationCardTypeEnum | null;
	saveVpcState: ConfirmationSummaryShareDesignStateEnum;
	emailValue: string;
	invalidEmailInput: boolean;
	emailState: SendByEmailStateEnum;
	qrCodeUrl?: string;
	showSaveForLater: boolean;
	selectedState: ShareDesignCardStateEnum | undefined;
	copyDesignCodeState: ShareDesignCardCopyStateEnum | undefined;
	copyDesignLinkState: ShareDesignCardCopyStateEnum;
	disableSmsAndEmail: boolean;
	onEmailInput?: (e: CustomEvent) => void;
	onEmailValidation?: (e: CustomEvent) => void;
	onSendEmail?: (e: CustomEvent) => void;
	onResetEmail?: () => void;
	onClose?: () => void;
	onSelect?: (e: CustomEvent) => void;
	onCopyDesignCodeClick?: (e: CustomEvent) => void;
	onCopyDesignCodeReset?: () => void;
	onCopyDesignLinkClick?: (e: CustomEvent) => void;
	onCopyDesignLinkReset?: () => void;
	onBackButtonClick?: () => void;
};

/**
 * React component.
 *
 */
export default class SaveView extends Component<Props> {
	/**
	 * React render method.
	 *
	 * @returns React node.
	 */
	public render(): ReactNode {
		const { props } = this;
		const {
			onClose,
			onEmailInput,
			onEmailValidation,
			onSendEmail,
			onResetEmail,
			onCopyDesignCodeClick,
			onCopyDesignCodeReset,
			onCopyDesignLinkClick,
			onCopyDesignLinkReset,
			onSelect,
			onBackButtonClick,
		} = props;
		return (
			<SkapaModal
				visible={props.showSaveForLater}
				handleCloseBtn={(): void => onClose && onClose()}
			>
				<SkapaSheets
					size={'small'}
					alignment={'right'}
					header={
						<SkapaModalHeader
							backBtnClick={
								props.selectedState !== ShareDesignCardStateEnum.default
									? (): void => onBackButtonClick && onBackButtonClick()
									: undefined
							}
						/>
					}
					footer={null}
					style={{
						zIndex: 10,
						borderTop:
							SkapaTheme.border.thickness.thin + 'px solid' + SkapaTheme.color.border.default,
						borderBottom:
							SkapaTheme.border.thickness.thin + 'px solid' + SkapaTheme.color.border.default,
					}}
				>
					<SkapaModalBody>
						{props.saveVpcState === ConfirmationSummaryShareDesignStateEnum.loading && (
							<LoadingLightboxContent>
								<Loading text={''}>
									<LoadingBall color={'emphasised'} size={'large'} />
								</Loading>
								<br />
								<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyXLarge}>
									{localeService.getTranslation('addToListLoading')}
								</KompisText>
							</LoadingLightboxContent>
						)}
						<>
							{props.kompisSettings && props.kompisTranslations && (
								<>
									{props.qrCodeUrl && (
										<KompisShareDesignCard
											theme={SkapaTheme}
											settings={props.kompisSettings}
											translations={props.kompisTranslations.kompis}
											locale={props.locale}
											autocomplete={props.platform === 'kiosk'}
											disableLinks={props.platform === 'kiosk'}
											state={props.selectedState}
											onSelect={(e: CustomEvent): void => onSelect && onSelect(e)}
											vpcCode={props.vpcCode}
											designLink={props.qrCodeUrl}
											sendByEmailState={props.emailState}
											sendByEmailValue={props.emailValue}
											sendByEmailInvalidInput={props.invalidEmailInput}
											onSendByEmailInput={(e: CustomEvent): void => onEmailInput && onEmailInput(e)}
											onSendByEmailValidation={(e: CustomEvent): void =>
												onEmailValidation && onEmailValidation(e)
											}
											onSendByEmailSend={(e: CustomEvent): void => onSendEmail && onSendEmail(e)}
											onSendByEmailReset={(): void => onResetEmail && onResetEmail()}
											showSendByEmail={!props.disableSmsAndEmail}
											showCopyDesignCodeButton={props.platform !== 'kiosk'}
											copyDesignCodeState={props.copyDesignCodeState}
											onCopyDesignCodeClick={(e: CustomEvent): void =>
												onCopyDesignCodeClick && onCopyDesignCodeClick(e)
											}
											onCopyDesignCodeReset={(): void =>
												onCopyDesignCodeReset && onCopyDesignCodeReset()
											}
											showGetLink={true}
											copyDesignLinkState={props.copyDesignLinkState}
											onCopyDesignLinkClick={(e: CustomEvent): void =>
												onCopyDesignLinkClick && onCopyDesignLinkClick(e)
											}
											onCopyDesignLinkReset={(): void =>
												onCopyDesignLinkReset && onCopyDesignLinkReset()
											}
											showQrCode={true}
										/>
									)}
								</>
							)}
						</>
						{props.confirmationCardType ===
							ConfigurationSummaryConfirmationCardTypeEnum.saveVpcFail && (
							<ErrorLightboxContent writeDirection={props.writeDirection}>
								<KompisIcon theme={SkapaTheme} icon={IncorrectIcon || ''}></KompisIcon>
								<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyMedium}>
									{localeService.getTranslation('codeFailureText')}
								</KompisText>
							</ErrorLightboxContent>
						)}
					</SkapaModalBody>
				</SkapaSheets>
			</SkapaModal>
		);
	}
}
